import React, { useState, memo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ChartChatMessage } from "../../chatModels";
import styles from "./ChartRenderer.module.css";
import DateTimeFormatter from "../../../app/DateTimeFormatter";
import { downloadCSV, downloadChartImage } from "./exportUtils";
import LightTooltip from "../../../../SolarGikLib/tooltip/LightTooltip";
import DownloadIcon from "../../icons/Download.svg?react";

interface IChartRendererProps {
  message: ChartChatMessage;
}

const utcDateFormatter = new DateTimeFormatter("UTC");

const ChartRendererMemoizedComponent: React.FC<IChartRendererProps> = ({ message }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { content: chartData } = message;
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      downloadCSV(chartData, utcDateFormatter);
      await downloadChartImage(message);
    } finally {
      setIsDownloading(false);
    }
  };

  const renderChart = () => {
    if (chartData.type === "line") {
      return (
        <ResponsiveContainer width="100%" height={180} className={styles.chartResponsiveContainer}>
          <LineChart data={chartData.data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="timestamp"
              stroke="#666"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              minTickGap={30}
              tickFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (Number.isNaN(date.getTime())) {
                    return value.toString();
                  }
                  return utcDateFormatter.formatDateAndTime(date) + " UTC";
                } catch {
                  return value.toString();
                }
              }}
            />
            <YAxis stroke="#666" />
            <Tooltip
              wrapperClassName={styles["tooltip-wrapper"]}
              labelClassName={styles["tooltip-label"]}
              labelFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (Number.isNaN(date.getTime())) {
                    return value.toString();
                  }
                  return utcDateFormatter.formatDateAndTime(date) + " UTC";
                } catch {
                  return value.toString();
                }
              }}
              formatter={(value, name) => {
                const unit = chartData.series.find((s) => s.key === name)?.valueUnit ?? "";
                const valueFixed = typeof value === "number" ? value.toFixed(2) : value;
                return [`${valueFixed} ${unit}`.trim(), name];
              }}
            />
            <Legend iconType="circle" iconSize={7} />
            {chartData.series.map((series) => (
              <Line
                key={series.key}
                type="monotone"
                dataKey={series.key}
                stroke={series.color}
                strokeWidth={2}
                dot={{ fill: series.color, strokeWidth: 1.5, r: 2 }}
                activeDot={{
                  r: 3,
                  stroke: series.color,
                  strokeWidth: 1.5,
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartData.type === "bar") {
      return (
        <ResponsiveContainer width="100%" height={180} className={styles.chartResponsiveContainer}>
          <BarChart data={chartData.data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="timestamp"
              stroke="#666"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              minTickGap={30}
              tickFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (Number.isNaN(date.getTime())) {
                    return value.toString();
                  }
                  return utcDateFormatter.formatDateAndTime(date) + " UTC";
                } catch {
                  return value.toString();
                }
              }}
            />
            <YAxis stroke="#666" />
            <Tooltip
              wrapperClassName={styles["tooltip-wrapper"]}
              labelClassName={styles["tooltip-label"]}
              labelFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (Number.isNaN(date.getTime())) {
                    return value.toString();
                  }
                  return utcDateFormatter.formatDateAndTime(date) + " UTC";
                } catch {
                  return value.toString();
                }
              }}
              formatter={(value, name) => {
                const unit = chartData.series.find((s) => s.key === name)?.valueUnit ?? "";
                const valueFixed = typeof value === "number" ? value.toFixed(2) : value;
                return [`${valueFixed} ${unit}`.trim(), name];
              }}
            />
            <Legend iconType="rect" iconSize={7} />
            {chartData.series.map((series) => (
              <Bar
                key={series.key}
                dataKey={series.key}
                fill={series.color}
                stroke={series.color}
                strokeWidth={1}
                radius={[2, 2, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return <div>Unsupported chart type: {chartData.type}</div>;
  };

  return (
    <div id={getChartContainerId(message)} className={styles["chart-container"]}>
      {chartData.title && <h3 className={styles["chart-title"]}>{chartData.title}</h3>}
      {chartData.description && <p className={styles["chart-description"]}>{chartData.description}</p>}
      {!isDownloading && chartData.data?.length > 0 && (
        <LightTooltip title="Download CSV & Image">
          <button className={styles["download-button"]} onClick={handleDownload}>
            <DownloadIcon width={20} height={20} />
          </button>
        </LightTooltip>
      )}
      {renderChart()}
    </div>
  );
};

export const getChartContainerId = (message: ChartChatMessage) => `chart-container-${message.id}`;

export const ChartRenderer = memo(ChartRendererMemoizedComponent);
